import SettingsSearchTarget from '@/components/Settings/SettingsSearchTarget';
import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { Themes, ThemeNames } from '@/constants/Colors';
import { capitalizeFirstLetter } from '@/constants/utils/text';

export function AppearanceGroup({ title, subtitle, children, card = true }) {
  const { colors } = useTheme();
  return <SettingsSearchTarget label={title} style={styles.group}>
    <View style={styles.groupHeading}>
      <Text size="l" weight="bold" accessibilityRole="header" accessibilityLevel={2}>{title}</Text>
      {!!subtitle && <Text size="s" color="secondary" style={styles.description}>{subtitle}</Text>}
    </View>
    <View style={card && [styles.card, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
      {children}
    </View>
  </SettingsSearchTarget>;
}

export function AppearanceRow({ title, subtitle, children, first = false, stacked = false }) {
  const { colors } = useTheme();
  return <SettingsSearchTarget label={title} accessibilityLabel={`${title} section`} style={[
    styles.row, { borderTopColor: colors.border_color_secondary }, first && { borderTopWidth: 0 },
    stacked && { flexDirection: 'column', alignItems: 'stretch' },
  ]}>
    <View style={styles.rowCopy}>
      <Text weight="medium">{title}</Text>
      {!!subtitle && <Text size="s" color="secondary" style={styles.description}>{subtitle}</Text>}
    </View>
    <View style={[styles.rowControl, stacked && { alignSelf: 'flex-start' }]}>{children}</View>
  </SettingsSearchTarget>;
}

function Choice({ label, selected, onPress, style, children }) {
  const { colors } = useTheme();
  const [focused, setFocused] = useState(false);
  return <Pressable accessibilityRole="button" accessibilityLabel={label}
    accessibilityState={{ selected }} onPress={onPress}
    {...(Platform.OS === 'web' ? { 'aria-pressed': selected } : {})}
    onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    style={({ hovered, pressed }) => [styles.choice, style, {
      borderColor: focused ? colors.text_color_link : selected ? colors.text_color_primary : colors.border_color_secondary,
      backgroundColor: hovered || pressed ? colors.solid_color_tertiary : colors.solid_color_block,
      opacity: pressed ? 0.8 : 1,
    }]}>{children}</Pressable>;
}

export function AppearanceThemePicker({ selected, onChange }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [width, setWidth] = useState(0);
  return <View style={styles.themes} onLayout={event => setWidth(event.nativeEvent.layout.width)}>
    {ThemeNames.map(name => {
      const palette = Themes[name];
      const active = selected === name;
      return <Choice key={name} label={t(`themes.${name}`)} selected={active} onPress={() => onChange(name)}
        style={[styles.themeChoice, { flexBasis: width >= 520 ? '17%' : '45%' }]}>
        <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants"
          style={[styles.miniWindow, { borderColor: palette.border_color_secondary, backgroundColor: palette.solid_color_primary }]}>
          <View style={[styles.miniSidebar, { backgroundColor: palette.solid_color_block }]}>
            {[0, 1, 2].map(line => <View key={line} style={[styles.miniLine, { backgroundColor: palette.text_color_secondary, opacity: line ? 0.25 : 0.6 }]} />)}
          </View>
          <View style={styles.miniContent}>
            <View style={[styles.miniBubble, { backgroundColor: palette.solid_color_secondary }]} />
            <View style={[styles.miniLine, { width: '82%', backgroundColor: palette.text_color_primary, opacity: 0.5 }]} />
            <View style={[styles.miniLine, { width: '58%', backgroundColor: palette.text_color_secondary, opacity: 0.35 }]} />
            <View style={[styles.miniInput, { borderColor: palette.border_color_secondary }]} />
          </View>
        </View>
        <View style={styles.themeLabel}>
          <Text size="s" weight={active ? 'bold' : 'normal'} style={{ flexShrink: 1 }}>{t(`themes.${name}`)}</Text>
          <View style={[styles.selectionMark, { backgroundColor: active ? colors.text_color_primary : 'transparent' }]}>
            {active && <Icon name="check" size={9} color={colors.solid_color_primary} />}
          </View>
        </View>
      </Choice>;
    })}
  </View>;
}

export function AppearanceColorPicker({ title, options, selected, onChange }) {
  const { colors } = useTheme();
  return <View accessibilityLabel={`${title} section`} style={styles.colorSection}>
    <View style={styles.colorHeading}>
      <Text weight="medium" style={{ flexShrink: 1 }}>{title}</Text>
      <Text size="s" color="secondary">{capitalizeFirstLetter(selected)}</Text>
    </View>
    <View style={styles.swatches}>
      {options.map(({ id, color }) => <Tooltip key={id} label={capitalizeFirstLetter(id)}>
        <Choice label={`${title}: ${capitalizeFirstLetter(id)}`} selected={selected === id} onPress={() => onChange(id)} style={styles.swatchChoice}>
          <View style={[styles.swatch, { borderColor: colors.border_color_secondary, backgroundColor: id === 'multicolor' ? colors.accent_color_primary : color }]}>
            {id === 'multicolor' && <View style={styles.multicolor}>
              {['#5E9BF7', '#AF7EE0', '#E8A35E', '#76BCA1'].map(tone => <View key={tone} style={{ width: '50%', height: '50%', backgroundColor: tone }} />)}
            </View>}
            {selected === id && <View style={[styles.swatchCheck, { backgroundColor: colors.text_color_primary }]}>
              <Icon name="check" size={10} color={colors.solid_color_primary} />
            </View>}
          </View>
        </Choice>
      </Tooltip>)}
    </View>
  </View>;
}

export function AppearanceChatPreview({ messageLayout, messageWidth, chatInputStyle, showTimestamps, showMessageActions }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const bubble = messageLayout === 'bubble';
  const slim = messageLayout === 'slim';
  const fixed = chatInputStyle === 'fixed';
  const detailed = chatInputStyle === 'detailed';
  return <View testID="appearance-chat-preview" style={[styles.preview, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary }]}>
    <View style={styles.previewLabel}>
      <Text size="xxs" weight="medium" color="secondary">{t('settings.appearance.design.preview')}</Text>
      <View style={[styles.previewBadge, { backgroundColor: colors.solid_color_tertiary }]}><Text size="xxs" color="secondary">{t('settings.appearance.design.live')}</Text></View>
    </View>
    <View testID="appearance-preview-conversation" style={[styles.previewConversation, {
      width: messageWidth === 'full' ? '100%' : messageWidth === 'medium' ? '91%' : '82%',
    }]}>
      <View style={[styles.previewUser, slim ? { alignSelf: 'flex-start', paddingHorizontal: 0, backgroundColor: 'transparent' } : { backgroundColor: colors.solid_color_user_bubble || colors.solid_color_secondary }]}>
        {slim && <Text size="xxs" weight="bold" color="secondary">{t('settings.appearance.design.sampleYou')}</Text>}
        <Text size="s" style={{ color: colors.text_color_user_bubble || colors.text_color_primary }}>{t('settings.appearance.design.samplePrompt')}</Text>
      </View>
      {showTimestamps && <Text size="xxs" color="secondary" style={{ alignSelf: 'flex-end' }}>{t('settings.appearance.design.sampleTime')}</Text>}
      <View testID="appearance-preview-reply" style={[styles.previewReply, {
        backgroundColor: bubble ? colors.solid_color_tertiary : 'transparent',
        padding: bubble ? 12 : 0, borderRadius: bubble ? 14 : 0,
      }]}>
        {slim && <Text size="xxs" weight="bold" color="secondary">Gab AI</Text>}
        <Text size="s" weight="bold">{t('settings.appearance.design.sampleTitle')}</Text>
        <Text size="s" style={styles.description}>{t('settings.appearance.design.sampleReply')}</Text>
        {showMessageActions && <View style={styles.previewActions} accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
          {['copy', 'arrow-up-from-bracket', 'ellipsis'].map(icon => <Icon key={icon} name={icon} size={12} color={colors.text_color_secondary} />)}
        </View>}
      </View>
      <View testID="appearance-preview-composer" style={[styles.previewComposer, {
        backgroundColor: colors.solid_color_tertiary, borderColor: colors.border_color_secondary,
        borderRadius: fixed ? 6 : 18, minHeight: detailed ? 72 : 42,
        flexDirection: detailed ? 'column' : 'row', alignItems: detailed ? 'stretch' : 'center',
      }]}>
        <Text size="xs" color="secondary" style={{ flex: 1 }}>{t('settings.appearance.design.samplePlaceholder')}</Text>
        <View style={[styles.previewComposerActions, detailed && { justifyContent: 'space-between' }]}>
          {detailed && <Icon name="plus" size={12} color={colors.text_color_secondary} />}
          <View style={[styles.previewSend, { backgroundColor: colors.accent_color_primary }]}>
            <Icon name="arrow-up" size={12} color={colors.accent_text_color} />
          </View>
        </View>
      </View>
    </View>
  </View>;
}

const styles = StyleSheet.create({
  group: { gap: 14, marginBottom: 30 },
  groupHeading: { gap: 4 },
  description: { lineHeight: 20, marginTop: 3 },
  card: { borderRadius: 18, borderWidth: 1, overflow: 'hidden' },
  row: { padding: 18, gap: 18, borderTopWidth: StyleSheet.hairlineWidth, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rowCopy: { flex: 1, minWidth: 0 },
  rowControl: { flexShrink: 0, alignSelf: 'center' },
  choice: { borderWidth: 2, borderRadius: 12 },
  themes: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  themeChoice: { flexGrow: 1, minWidth: 0, padding: 7, gap: 8 },
  miniWindow: { height: 62, borderWidth: 1, borderRadius: 6, flexDirection: 'row', overflow: 'hidden' },
  miniSidebar: { width: '23%', paddingHorizontal: 4, paddingTop: 12, gap: 5 },
  miniContent: { flex: 1, padding: 7, gap: 5 },
  miniLine: { height: 3, borderRadius: 3 },
  miniBubble: { height: 11, width: '65%', borderRadius: 4, alignSelf: 'flex-end' },
  miniInput: { height: 8, borderWidth: 1, borderRadius: 4, marginTop: 1 },
  themeLabel: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2, paddingHorizontal: 2, paddingBottom: 2 },
  selectionMark: { width: 16, height: 16, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  colorSection: { gap: 12, paddingVertical: 16 },
  colorHeading: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  swatches: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  swatchChoice: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  swatch: { width: 28, height: 28, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  multicolor: { position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, flexDirection: 'row', flexWrap: 'wrap' },
  swatchCheck: { width: 18, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  preview: { padding: 18, borderBottomWidth: 1 },
  previewLabel: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 },
  previewBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  previewConversation: { maxWidth: 560, alignSelf: 'center', gap: 9 },
  previewUser: { alignSelf: 'flex-end', paddingHorizontal: 12, paddingVertical: 9, borderRadius: 16, maxWidth: '94%' },
  previewReply: { alignSelf: 'stretch', gap: 3 },
  previewActions: { flexDirection: 'row', gap: 14, marginTop: 7 },
  previewComposer: { borderWidth: 1, marginTop: 8, paddingHorizontal: 12, paddingVertical: 8, gap: 8 },
  previewComposerActions: { flexDirection: 'row', alignItems: 'center' },
  previewSend: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
});
